import { isSameRace, isTrainingRace } from './raceValueUtils';

export type ManeuverTrainingRacingFilter = 'TRAINING' | 'RACING' | null | undefined;

/** Read race number from a maneuver table row (API fields are lowercase-first with fallbacks). */
export function maneuverRowRaceNumber(row: Record<string, unknown>): unknown {
  return row.race_number ?? row.Race_number ?? row.race ?? row.Race ?? null;
}

/** Read leg number from a maneuver/performance row (lowercase-first with fallbacks). */
export function maneuverRowLegNumber(row: Record<string, unknown>): unknown {
  return row.leg_number ?? row.Leg_number ?? row.LEG ?? null;
}

/**
 * True unless the row has an explicit non-racing leg (leg number present and <= 0,
 * e.g. prestart 0 / training -1). A missing or non-numeric leg is allowed, so genuine
 * race maneuvers that don't carry a leg tag are not dropped.
 */
export function isRacingLegRow(row: Record<string, unknown>): boolean {
  const legValue = maneuverRowLegNumber(row);
  if (legValue == null || legValue === '') return true;
  const num = Number(legValue);
  if (!Number.isFinite(num)) return true;
  return num > 0;
}

/** True when the row's race value represents training (-1, 0, TRAINING, etc.). */
export function isManeuverTrainingRaceRow(row: Record<string, unknown>): boolean {
  const raceValue = maneuverRowRaceNumber(row);
  if (raceValue == null || raceValue === '') return false;
  return isTrainingRace(raceValue);
}

/** True when the row is a race maneuver (positive race number). */
export function isManeuverRacingRaceRow(row: Record<string, unknown>): boolean {
  const raceValue = maneuverRowRaceNumber(row);
  if (raceValue == null || raceValue === '') return false;
  if (isTrainingRace(raceValue)) return false;
  const num = Number(raceValue);
  return Number.isFinite(num) && num > 0;
}

/** Apply Training / Racing filter to a maneuver row. */
export function passesManeuverTrainingRacingFilter(
  row: Record<string, unknown>,
  trainingRacing: ManeuverTrainingRacingFilter
): boolean {
  if (!trainingRacing) return true;
  if (trainingRacing === 'TRAINING') return isManeuverTrainingRaceRow(row);
  // RACING = positive race number AND a real racing leg (leg_number > 0).
  return isManeuverRacingRaceRow(row) && isRacingLegRow(row);
}

/** Apply selected race chips (includes TRAINING alias handling via isSameRace). */
export function passesManeuverRaceFilter(
  row: Record<string, unknown>,
  selectedRaces: (string | number)[]
): boolean {
  if (selectedRaces.length === 0) return true;
  const raceValue = maneuverRowRaceNumber(row);
  if (raceValue == null || raceValue === '') return false;
  return selectedRaces.some((selected) => isSameRace(raceValue, selected));
}

/** Drop training race selections when switching to RACING mode. */
export function stripTrainingRaceSelections<T extends string | number>(races: T[]): T[] {
  return races.filter(
    (r) => r !== 'TRAINING' && r !== 'training' && r !== '-1' && Number(r) !== -1 && Number(r) !== 0
  );
}
